import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  createAuthenticatedApi,
  getApiErrorMessage,
  throwIfResultFailed,
} from "@/lib/utils";
import { UsersApi } from "@/openapi/generated";

export const useEmployeeRoleUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const users = createAuthenticatedApi(UsersApi, accessToken);
      const result = await users.apiUserUserIdEmployeeRolePut({
        userId,
        setEmployeeRoleRequest: { role },
      });
      throwIfResultFailed(result, "Failed to update the employee role");
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Teachers] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Teacher] });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersScrollGet],
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.UserGet] });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersByRoleGet],
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to update the employee role",
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { updateRole: mutateAsync, isUpdatingRole: isPending };
};
